import React from 'react';
import { Button } from '@iterable/deprecated_react_components';
import DragAndDropList from 'core/dragAndDropList';
import { Drag } from 'ui/icons';
import { ModalHeader, ModalBody, ModalFooter, MODAL_SIZES } from 'ui/modal';
import noop from 'utils/noop';
import { arrayToObject } from 'utils/helpers';
import {
  StyledCoreModal,
  StyledDraggable,
  ItemContainer,
  StyledSemanticCheckbox,
  StyledDroppable,
} from './ToggleColumnsModal.styled';
import {
  shouldStateUpdate,
  getSelectedColumns,
  getChangeToggleColumns,
  getHandleReorderColumns,
} from './ToggleColumnsModal.utils';

// type Props = {
//   /** Columns of the table. */
//   columns: [],
//   /** If the left-most column is docked. */
//   leftColumnDocked: boolean,
//   /** If the modal is opened. */
//   open: boolean,
//   /**
//    * Callback function when modal is closed.
//    *
//    * ```
//    * onClose() => {}
//    * ```
//    * @returns { void }
//    */
//   onClose: () => {},
//   /**
//    * Callback function when save button is clicked.
//    *
//    * ```
//    * onSave(e, props) => {}
//    * ```
//    * @param { Object } e: The click event.
//    * @param { Object } props: The updated props included the new visibleColumns.
//    * @returns { void }
//    */
//   onSave: (e: {}, props: {}) => {},
//   /** Currently visible columns. If empty, all columns are visible. */
//   visibleColumns: [],
// };

export default class ToggleColumnsModal extends React.Component {
  static defaultProps = {
    columns: [],
    onSave: noop,
    visibleColumns: [],
  };

  constructor(props) {
    super(props);

    this.state = {
      selectedColumns: [],
    };
  }

  /**
   * shouldComponentUpdate
   * Updates the selected columns value when modal is open.
   */
  shouldComponentUpdate = (nextProps) => {
    if (shouldStateUpdate(nextProps, this.props)) {
      this.setSelectedColumns();
      return false;
    }
    return true;
  };

  /**
   * setSelectedColumns
   * From the passed in `visibleColumns` props, set the selected columns value by filtering from `columns`.
   */
  setSelectedColumns = () => {
    const stateToUpdate = getSelectedColumns(this.props);
    this.setState(stateToUpdate);
  };

  /**
   * handleChangeToggle
   * Callback function when a toggle is changed.
   */
  handleChangeToggle = (e, { checked, id, value }) => {
    const stateToUpdate = getChangeToggleColumns(this.state, { checked, id, value });
    this.setState(stateToUpdate);
  };

  /**
   * handleReorder
   * Callback function when the columns are reordered.
   * @param { Number } itemIndex: The index of the column changed.
   * @param { Number } newIndex: The new index of this column.
   * @returns { void }
   */
  handleReorder = (itemIndex, newIndex) => {
    const stateToUpdate = getHandleReorderColumns(this.state, itemIndex, newIndex);
    this.setState(stateToUpdate);
  };

  /**
   * handleSave
   * Callback function when save button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleSave = (e) => {
    const { selectedColumns, reordered } = this.state;
    const { columns, onSave, ...otherProps } = this.props;

    const hiddenColumns = [];
    columns.forEach((column) => {
      if (!selectedColumns.includes(column.value)) {
        hiddenColumns.push(column.value);
      }
    });

    onSave(e, { ...otherProps, visibleColumns: selectedColumns, hiddenColumns, reordered });
  };

  /**
   * createItem
   * Creates a column item in the list.
   * @param { Object } itemProps: The props of the column item.
   * @param { Number } index: The index of the item.
   * @param { Boolean } disabled: If the item is disabled.
   * @returns { React.Component } The item in the columns list.
   */
  createItem = (itemProps, index, disabled) => {
    const { checked, disableToggle, hideToggle, name, value } = itemProps;
    const { leftColumnDocked } = this.props;
    return (
      <StyledDraggable
        key={index}
        id={`${name}${value}`}
        index={index}
        disabled={disabled}
        draggingClass="dragging"
      >
        <ItemContainer>
          <StyledSemanticCheckbox
            checked={checked}
            readOnly={disableToggle || (leftColumnDocked && index === 0) || hideToggle}
            id={index}
            label={name}
            onChange={this.handleChangeToggle}
            toggle
            value={value}
          />
          {!disabled && (
            <div className="handle">
              <Drag />
            </div>
          )}
        </ItemContainer>
      </StyledDraggable>
    );
  };

  /**
   * generateColumnSelector
   * Builds out the selection list of columns to toggle.
   * @returns { React.Component } The selection list of columns.
   */
  generateColumnSelector = () => {
    const { selectedColumns } = this.state;
    const { columns, leftColumnDocked } = this.props;
    let index = 0;

    const columnsObject = arrayToObject(columns, 'value');
    const hiddenColumns = Object.keys(columnsObject).filter(
      (column) => !selectedColumns.includes(column),
    );
    const firstItem = this.createItem(
      { ...columnsObject[selectedColumns[0]], checked: true },
      index,
      leftColumnDocked,
    );
    const items = selectedColumns.slice(1).map((column) => {
      index += 1;
      return this.createItem({ ...columnsObject[column], checked: true }, index);
    });
    const hiddenItems = hiddenColumns.map((column) => {
      index += 1;
      return this.createItem({ ...columnsObject[column], checked: false }, index, true);
    });

    return (
      <DragAndDropList onReorder={this.handleReorder}>
        {leftColumnDocked && (
          <StyledDroppable className="disabledItemsTop" disabled id="0">
            {firstItem}
          </StyledDroppable>
        )}
        <StyledDroppable id="1">{leftColumnDocked ? items : [firstItem, ...items]}</StyledDroppable>
        <StyledDroppable className="disabledItems" disabled id="2">
          {hiddenItems}
        </StyledDroppable>
      </DragAndDropList>
    );
  };

  render() {
    const { open, onClose } = this.props;

    const modalActions = [
      <Button key="cancel" appearance="default" data-cancel-modal onClick={onClose}>
        Cancel
      </Button>,
      <Button key="save" appearance="primary" onClick={this.handleSave}>
        Apply
      </Button>,
    ];

    return open ? (
      <StyledCoreModal isModalOpen={open} onClose={onClose} size={MODAL_SIZES.SMALL}>
        <ModalHeader>Customize Columns</ModalHeader>
        <ModalBody>{this.generateColumnSelector()}</ModalBody>
        <ModalFooter hasCancel={false}>{modalActions}</ModalFooter>
      </StyledCoreModal>
    ) : null;
  }
}
